import * as React from "react";
import { UitkCard, UitkCardContentSection } from "@egds/react-core/cards";
import { UitkFigure, UitkFigureAspectRatioType } from "@egds/react-core/images";
import { UitkButton, UitkPrimaryButton } from "@egds/react-core/button";
import { UitkIcon } from "@egds/react-core/icons";
import { Viewport, ViewSmall, ViewLarge } from "@shared-ui/viewport-context";
import { useLocalization } from "@shared-ui/localization-context";
import { MapMarkerCardProps, MapMarkerCardViewProps, MarkerType } from "../typings";

import { FlexClickTracker } from "src/components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { Action, sendImmediateTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { UitkHeading, UitkText } from "@egds/react-core/text";
import { UitkLayoutFlex } from "@egds/react-core/layout-flex";
import { UitkSpacing } from "@egds/react-core/spacing";
import { BlossomImage } from "src/components/shared/BlossomImage/BlossomImage";

const ButtonTracked = FlexClickTracker(UitkPrimaryButton);

export const MAP_CARD_ID = "dx-map-card";
export const MAP_CARD_IMAGE_ID = "dx-map-card-image";
const moduleName = "MapMarkerCard";

export const MapMarkerCard = (props: MapMarkerCardProps) => {
  return (
    <Viewport>
      <ViewSmall>
        <MapMarkerCardView showCard={props.showCard} onClose={props.onClose} content={props.content} isMobile />
      </ViewSmall>
      <ViewLarge>
        <MapMarkerCardView showCard={props.showCard} onClose={props.onClose} content={props.content} isMobile={false} />
      </ViewLarge>
    </Viewport>
  );
};

export const MapMarkerCardView = (props: MapMarkerCardViewProps) => {
  const { formatText } = useLocalization();
  const cardClasses = props.isMobile ? "map-card" : "map-card map-card--desktop";

  const closeMapMarkerCard = () => {
    const trackingInfo = {
      moduleName,
      action: Action.CLICK,
      rfrr: "mapMarker.close",
    };

    sendImmediateTrackEvent(trackingInfo, props.analytics);
    props.onClose();
  };

  const priceText = () => {
    const { price, type } = props.content;

    if (!price) {
      return null;
    }

    if (!(type === MarkerType.HOTEL || type === MarkerType.ACTIVITY)) {
      return null;
    }

    const textToDisplay =
      type === MarkerType.HOTEL
        ? formatText("destinationDiscoveryMap.hotel.price", price)
        : formatText("destinationDiscoveryMap.activity.price", price);

    return (
      <UitkCardContentSection border="bottom">
        <UitkText size={500} weight="medium" align="right">
          {textToDisplay}
        </UitkText>
      </UitkCardContentSection>
    );
  };

  return (
    <div id={MAP_CARD_ID} className={props.showCard ? `${cardClasses} map-card--reveal` : cardClasses}>
      <UitkCard>
        <UitkLayoutFlex direction="column">
          <UitkCardContentSection border="bottom">
            <UitkLayoutFlex justifyContent="space-between" alignItems="center">
              <UitkHeading size={5} tag="h2">
                {props.content.title}
              </UitkHeading>
              <UitkSpacing padding="one">
                <UitkButton
                  title={formatText("destinationDiscoveryMap.button.card.close")}
                  onClick={closeMapMarkerCard}
                >
                  <UitkIcon name="close" />
                </UitkButton>
              </UitkSpacing>
            </UitkLayoutFlex>
          </UitkCardContentSection>

          {Boolean(props.content.photoUrl) && (
            <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
              <BlossomImage
                placeholderImage
                alt={props.content.attribution?.attributionAltText || props.content.photoAlt || ""}
                src={props.content.photoUrl!!}
              />
              {props.content.attribution?.attributionText}
            </UitkFigure>
          )}

          {priceText()}

          <UitkCardContentSection>
            <UitkLayoutFlex direction="column">
              <UitkText>{props.content.description}</UitkText>
              <UitkSpacing margin={{ blockstart: "three" }}>
                <ButtonTracked
                  moduleName="findHotel"
                  rfrr="map.marker"
                  linkName="RFRR Action Event"
                  href={props.content.link}
                >
                  <UitkIcon name={props.content.buttonIcon} />
                  {props.content.buttonLabel}
                </ButtonTracked>
              </UitkSpacing>
            </UitkLayoutFlex>
          </UitkCardContentSection>
        </UitkLayoutFlex>
      </UitkCard>
    </div>
  );
};

export default MapMarkerCard;
